import {
  ActionIcon,
  Button,
  Group,
  Indicator,
  Modal,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import { useLanguage } from "../../../../../stores/LanguageStore";
import { useEffect, useMemo, useState } from "react";
import { findList } from "../../../../../api/ContactAPI";
import { notifications } from "@mantine/notifications";
import {
  IconAlertCircle,
  IconCaretUpFilled,
  IconCheck,
  IconCircleMinus,
  IconClock,
  IconLoader,
} from "@tabler/icons-react";
import JobItemCompleted from "./JobItemCompleted";
import JobItemInProgress from "./JobItemInProgress";
import JobItemCancelled from "./JobItemCancelled";
import JobItemFailed from "./JobItemFailed";
import JobItemInQueue from "./JobItemInQueue";
import ListRefreshTargeting from "../../actions/ListRefreshTargeting";
import ListRefreshTargetingGoogle from "../../actions/ListRefreshTargetingGoogle";
import { getLinkedinAccounts } from "../../../../../api/EmailAPI";
import { Link } from "react-router-dom";
import { getPermission } from "../../../../../functions/utilFunctions";
import { useListStore } from "../../../../../stores/ListStore";

const updateDelay = 5000;

const Jobs = ({
  row,
  setCurrentList,
  apiRef,
  isContent,
  isZapier,
  isPrm,
  onDiff,
  isList,
  setLoading,
  inColumn = true,
  isCron,
  handleZapierLoad,
}) => {
  const lang = useLanguage((s) => s.language);

  const [completed, setCompleted] = useState(
    row ? row?.state_details?.filter((x) => x.state === "Completed") : []
  );
  const [failed, setFailed] = useState(
    row
      ? row?.state_details?.filter(
        (x) =>
          x.state === "Failed" &&
          x?.error !== "No more emails generated available for this month"
      )
      : []
  );
  const [needUpgrade, setNeedUpgrade] = useState(
    row
      ? row?.state_details?.filter((x) => {
        return (
          x.state === "Failed" &&
          [
            `{"state":false,"state_message":"max_datafield_ai_per_month_reached"}`,
            `{"state":false,"state_message":"max_emails_generated_per_month_reached"}`,
            `{"state":false,"state_message":"max_icebreaker_per_month_reached"}`,
            `{"state":false,"state_message":"max_mobile_phones_per_month_reached"}`,
          ].includes(x?.error)
        );
      })
      : []
  );
  const [inQueue, setInQueue] = useState(
    row ? row?.state_details?.filter((x) => x.state === "InQueue") : []
  );
  const [inProgress, setInProgress] = useState(
    row ? row?.state_details?.filter((x) => x.state === "InProgress") : []
  );
  const [cancelled, setCancelled] = useState(
    row ? row?.state_details?.filter((x) => x.state === "Cancelled") : []
  );

  const props = useMemo(() => {
    return {
      completed: {
        value: completed,
        color: "tertiary.8",
        icon: <IconCheck size={inColumn ? 16 : 20} />,
      },
      in_progress: {
        value: inProgress,
        color: "primary",
        icon: <IconLoader size={inColumn ? 16 : 20} />,
      },
      in_queue: {
        value: inQueue,
        color: "orange",
        icon: <IconClock size={inColumn ? 16 : 20} />,
      },
      cancelled: {
        value: cancelled,
        color: "purple",
        icon: <IconCircleMinus size={inColumn ? 16 : 20} />,
      },
      error: {
        value: failed,
        color: "red",
        icon: <IconAlertCircle size={inColumn ? 16 : 20} />,
      },
      needUpgrade: {
        value: needUpgrade,
        color: "orange",
        icon: <IconCaretUpFilled size={inColumn ? 14 : 18} />,
      },
    };
  }, [
    cancelled,
    completed,
    failed,
    inColumn,
    inProgress,
    inQueue,
    needUpgrade,
  ]);

  const [type, setType] = useState(null);
  const handleClick = (type) => {
    setType(type);
  };
  const handleClose = () => {
    setType(null);
  };

  useEffect(() => {
    setCompleted(
      row ? row?.state_details?.filter((x) => x.state === "Completed") : []
    );
    setFailed(
      row
        ? row?.state_details?.filter(
          (x) =>
            x.state === "Failed" &&
            x?.error !== "No more emails generated available for this month"
        )
        : []
    );
    setNeedUpgrade(
      row
        ? row?.state_details?.filter((x) => {
          return (
            x.state === "Failed" &&
            [
              `{"state":false,"state_message":"max_datafield_ai_per_month_reached"}`,
              `{"state":false,"state_message":"max_emails_generated_per_month_reached"}`,
              `{"state":false,"state_message":"max_icebreaker_per_month_reached"}`,
              `{"state":false,"state_message":"max_mobile_phones_per_month_reached"}`,
            ].includes(x?.error)
          );
        })
        : []
    );
    setInQueue(
      row ? row?.state_details?.filter((x) => x.state === "InQueue") : []
    );
    setInProgress(
      row ? row?.state_details?.filter((x) => x.state === "InProgress") : []
    );
    setCancelled(
      row ? row?.state_details?.filter((x) => x.state === "Cancelled") : []
    );
  }, [row]);

  useEffect(() => {
    if (
      row?.state_details?.find(
        (x) => x.state === "InProgress" || x.state === "InQueue"
      )
    ) {
      const interval = setInterval(() => {
        if (isPrm) {
          setLoading(true);
        } else if (isZapier) {
          handleZapierLoad?.();
        } else if (isCron) {
          if (document.getElementById("cron-reload"))
            document.getElementById("cron-reload")?.click();
        } else {
          findList(row.id).then((response) => {
            let newProfile = response.data.contact_list_profile;
            if (apiRef) {
              apiRef?.current?.updateRows?.([{ ...newProfile }]);
            }

            setCurrentList?.(newProfile);
            let diff =
              row.state_details.filter((x) => x.state === "Completed").length <
              newProfile.state_details.filter((x) => x.state === "Completed")
                .length;

            if (diff) {
              onDiff?.();
              notifications.show({
                message: `${lang.contact_lists.jobs.success_text1} ${row?.name || ""
                  } #${row?.id} ${lang.contact_lists.jobs.success_text2}`,
              });
            }
          });
        }
      }, updateDelay);
      return () => clearInterval(interval);
    }
  }, [
    row?.state_details,
    row?.name,
    row?.id,
    apiRef,
    lang,
    isZapier,
    isContent,
    isPrm,
    setCurrentList,
    onDiff,
    isCron,
    handleZapierLoad,
  ]);

  useEffect(() => {
    if (
      !apiRef &&
      !inColumn &&
      row?.state_details?.find(
        (x) => x.type.includes("inked") && x.state === "Failed"
      )
    ) {
      getLinkedinAccounts();
    }
  }, [apiRef, inColumn]);

  return (
    <>
      <Group wrap="nowrap">
        {Object.keys(props).map((key) => {
          let values = props[key].value;
          let color = props[key].color;
          let icon = props[key].icon;

          if (values?.length > 0) {
            if (key === "needUpgrade") {
              return (
                <Tooltip label={getPermission("displayPricing") ? lang.contact_lists.jobs.upgrade_text : lang.contact_lists.jobs.upgrade_text2} key={key}>
                  <Button
                    variant="light"
                    color={color}
                    component={
                      getPermission("displayPricing") ? Link : undefined
                    }
                    to={"/settings/subscription"}
                    size={"xs"}
                    radius={"lg"}
                    fw={500}
                    mt={4}
                    mih={0}
                    h={24}
                    leftSection={icon}
                    key={key}
                  >
                    Upgrade
                  </Button>
                </Tooltip>
              );
            }
            return (
              <Group key={key} wrap="nowrap">
                <Tooltip
                  label={
                    values.length +
                    ` ${lang.contact_lists.jobs.job_name}${values?.length > 1 ? "s" : ""
                    } ${lang.contact_lists.jobs[key].name}`
                  }
                >
                  <Indicator
                    size={
                      values?.length > 9 ? (values?.length > 99 ? 24 : 18) : 14
                    }
                    styles={{
                      indicator: {
                        fontSize: 8,
                      },
                    }}
                    label={values?.length > 99 ? "99+" : values?.length}
                    offset={1}
                    position="top-start"
                    color={color}
                    mt={isContent ? 0 : 5}
                  >
                    <ActionIcon
                      variant="light"
                      color={color}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClick(key);
                      }}
                      size={"sm"}
                      radius={"md"}
                      w={inColumn ? 24 : 32}
                      h={inColumn ? 24 : 32}
                    >
                      {icon}
                    </ActionIcon>
                  </Indicator>
                </Tooltip>

                {isList &&
                  key === "error" &&
                  row.state_details.find(
                    (x) => x.type.includes("inked") && x.state === "Failed"
                  ) && (
                    <ListRefreshTargeting
                      closeMenu={() => { }}
                      current={row}
                      inJob
                      onFinish={() => {
                        useListStore.getState().setListingLoad(true)
                        useListStore.getState().setLoadProfile(true)
                        setLoading(true)
                      }}
                    />
                  )}

                {isList &&
                  key === "error" &&
                  row.state_details.find(
                    (x) => x.type.includes("oogl") && x.state === "Failed"
                  ) && (
                    <ListRefreshTargetingGoogle
                      closeMenu={() => { }}
                      current={row}
                      inJob
                      onFinish={() => {
                        useListStore.getState().setListingLoad(true)
                        useListStore.getState().setLoadProfile(true)
                        setLoading(true)
                      }}
                    />
                  )}
              </Group>
            );
          }
          return "";
        })}
      </Group>

      {!!type && (
        <Modal
          opened={!!type}
          onClose={handleClose}
          title={
            <Text maw={500} truncate="end" mr={16}>
              {
                lang.contact_lists.jobs[type === "needUpgrade" ? "error" : type]
                  .title
              }{" "}
              {isPrm ? (
                lang.prm.browser_tab_title
              ) : (
                <b>
                  {row.name} #{row.id}
                </b>
              )}
            </Text>
          }
          size={"52%"}
        >
          <Stack>
            {props[type]?.value.length > 0 ? (
              props[type]?.value.map((item, i) => {
                if (type === "completed") {
                  return (
                    <JobItemCompleted key={item?.uniqid || i} item={item} />
                  );
                }
                if (type === "in_progress") {
                  return (
                    <JobItemInProgress key={item?.uniqid || i} item={item} />
                  );
                }
                if (type === "in_queue") {
                  return <JobItemInQueue key={item?.uniqid || i} item={item} />;
                }
                if (type === "cancelled") {
                  return (
                    <JobItemCancelled key={item?.uniqid || i} item={item} />
                  );
                }
                if (type === "error" || type === "needUpgrade") {
                  return (
                    <JobItemFailed
                      key={item?.uniqid || i}
                      item={item}
                      setFailed={setFailed}
                    />
                  );
                }
                return "";
              })
            ) : (
              <Text c={"dimmed"}>{lang.contact_lists.jobs[type].no_job}</Text>
            )}
          </Stack>
        </Modal>
      )}
    </>
  );
};

export default Jobs;
